import { useCheckQuestionCatalogue } from '@my/check-api/hooks';
import type { CheckRegime, UnitType, WacType } from '@my/check-api';
import { Button, TextInput } from '@my/ui';
import { Check, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { unitTypesFor } from './lib/templateTypes';

interface NewTemplateWizardProps {
  onCancel: () => void;
  onBuild: (input: {
    name: string;
    regime: CheckRegime;
    unitType: UnitType;
    wacType: WacType | null;
  }) => void;
  isSaving: boolean;
}

/**
 * Step 1 — pick a regime, unit and answer type. The unit list switches on
 * regime: WAC covers HGV through Minibus, Pre-Use covers Car, Motorcycle and
 * Cycle. WAC type only renders for the WAC regime, since Pre-Use has no
 * Type 1 / Type 2 distinction.
 *
 * The default-question count comes from the BE catalogue rather than a local
 * copy, so the footer can never disagree with what the create call actually
 * forks.
 */
export function NewTemplateWizard({ onCancel, onBuild, isSaving }: NewTemplateWizardProps) {
  const { t } = useTranslation('checks');

  const [name, setName] = useState('');
  const [regime, setRegime] = useState<CheckRegime>('Wac');
  const [unit, setUnit] = useState<UnitType>('Hgv');
  const [wacType, setWacType] = useState<WacType>('Type1');

  const units = unitTypesFor(regime);
  const catalogue = useCheckQuestionCatalogue(unit);

  /** Changing regime invalidates the unit — reset to the new list's first entry. */
  const pickRegime = (next: CheckRegime) => {
    if (next === regime) return;
    setRegime(next);
    const first = unitTypesFor(next)[0];
    if (first) setUnit(first.key);
  };

  const canBuild = name.trim().length > 0 && !isSaving;
  const count = catalogue.data?.questions.length;
  const unitName = units.find((u) => u.key === unit)?.label ?? unit;
  const typeName =
    regime === 'Wac'
      ? t(wacType === 'Type1' ? 'templates.wac.type1' : 'templates.wac.type2')
      : t('templates.regime.preUseShort');

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{t('templates.new.title')}</h2>
          <p className="text-xs text-slate-600">{t('templates.new.description')}</p>
        </div>

        <Field label={t('templates.new.nameLabel')}>
          <TextInput
            name="template-name"
            value={name}
            maxLength={128}
            placeholder={t('templates.new.namePlaceholder')}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field label={t('templates.new.regimeLabel')}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <OptionCard
              selected={regime === 'Wac'}
              title={t('templates.regime.wac')}
              description={t('templates.regime.wacDesc')}
              onClick={() => pickRegime('Wac')}
            />
            <OptionCard
              selected={regime === 'PreUse'}
              title={t('templates.regime.preUse')}
              description={t('templates.regime.preUseDesc')}
              onClick={() => pickRegime('PreUse')}
            />
          </div>
        </Field>

        <Field label={t('templates.new.unitLabel')}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {units.map((u) => (
              <OptionCard
                key={u.key}
                selected={unit === u.key}
                icon={u.icon}
                title={u.label}
                description={u.description}
                onClick={() => setUnit(u.key)}
              />
            ))}
          </div>
        </Field>

        {regime === 'Wac' && (
          <Field label={t('templates.new.wacTypeLabel')}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <OptionCard
                selected={wacType === 'Type1'}
                title={t('templates.wac.type1')}
                description={t('templates.wac.type1Desc')}
                onClick={() => setWacType('Type1')}
              />
              <OptionCard
                selected={wacType === 'Type2'}
                title={t('templates.wac.type2')}
                description={t('templates.wac.type2Desc')}
                onClick={() => setWacType('Type2')}
              />
            </div>
          </Field>
        )}

        {/* Count comes from the catalogue endpoint, so it always matches what
            the create call will actually fork. */}
        <p className="rounded-sm border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {catalogue.isError
            ? t('templates.new.summaryUnavailable')
            : count === undefined
              ? t('templates.new.summaryLoading')
              : t('templates.new.summary', { count, unit: unitName, type: typeName })}
        </p>
      </div>

      <footer className="flex items-center justify-between gap-2 border-t border-slate-200 py-3">
        <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
          {t('templates.new.cancel')}
        </Button>
        <Button
          rightIcon={ChevronRight}
          disabled={!canBuild}
          title={name.trim() ? undefined : t('templates.new.nameRequired')}
          onClick={() =>
            onBuild({
              name: name.trim(),
              regime,
              unitType: unit,
              wacType: regime === 'Wac' ? wacType : null,
            })
          }
        >
          {isSaving ? t('templates.new.building') : t('templates.new.build')}
        </Button>
      </footer>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-slate-600">{label}</p>
      {children}
    </div>
  );
}

/** Selectable card. Selection reads the same across all three groups. */
function OptionCard({
  selected,
  icon: Icon,
  title,
  description,
  onClick,
}: {
  selected: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex w-full items-start gap-2 rounded-sm border px-3 py-2.5 text-left transition-colors ${
        selected ? 'border-blue-400 bg-blue-50/60' : 'border-slate-300 bg-white hover:bg-slate-50'
      }`}
    >
      {Icon && (
        <Icon
          className={`mt-0.5 h-4 w-4 flex-shrink-0 ${selected ? 'text-blue-600' : 'text-slate-400'}`}
        />
      )}
      <span className="min-w-0 flex-1">
        <span
          className={`flex items-center gap-1 text-sm font-semibold ${
            selected ? 'text-blue-700' : 'text-slate-900'
          }`}
        >
          {title}
          {selected && <Check className="h-3 w-3 flex-shrink-0" aria-hidden="true" />}
        </span>
        <span className="mt-0.5 block text-[10px] text-slate-500">{description}</span>
      </span>
    </button>
  );
}
